import React, { useRef, useEffect, useState } from 'react';

import Head from 'design-system/Head';
import { WithCopy } from '@scale/frontend-lib/sensor-fusion';
import { Typography } from '@scale/scaleui';
import { ISensorFusionSubstageDefinitionLookupDto } from '@scale/sensor-fusion-shared';

import { ReactJsonView } from 'react-scale/corp/components/ReactJsonView';
import HideableSection from 'react-scale/corp/components/UI/HideableSection';
import { DOUBLE_COLUMN_MIN_WIDTH } from '../constants';
import { openSelectedIfUrl } from '../helpers/url';
import SubstageDefinitionBreakdownPanel from './SubstageDefinitionBreakdownPanel';
import SubstageDefinitionOverviewPanel from './SubstageDefinitionOverviewPanel';
import SubstageDefinitionToolsPanel from './SubstageDefinitionToolsPanel';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

type SubstageDefinitionLookupViewProps = {
  substageDefinition: ISensorFusionSubstageDefinitionLookupDto;
};

const SubstageDefinitionLookupView = ({
  substageDefinition,
}: SubstageDefinitionLookupViewProps) => {
  const myRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);

  const { isRemoAdmin } = useRemoAdminCtx();

  useEffect(() => {
    const updateWidth = () => {
      setWidth(myRef?.current?.getBoundingClientRect().width);
    };

    window.addEventListener('resize', updateWidth);
    updateWidth();
    return () => {
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  const leftColumnElements = (
    <>
      <SubstageDefinitionOverviewPanel substageDefinition={substageDefinition} />
      {!isRemoAdmin && (
        <>
          <SubstageDefinitionToolsPanel substageDefinition={substageDefinition} />
          <HideableSection name="JSON">
            <ReactJsonView
              src={substageDefinition}
              name={false}
              theme="monokai"
              iconStyle="triangle"
              collapsed={1}
              onSelect={openSelectedIfUrl}
              collapseStringsAfterLength={120}
              groupArraysAfterLength={false}
              enableClipboard={true}
              displayObjectSize={false}
              displayDataTypes={false}
            />
          </HideableSection>
        </>
      )}
    </>
  );
  const rightColumnElements = (
    <>
      <SubstageDefinitionBreakdownPanel substageDefinition={substageDefinition} />
    </>
  );

  return (
    <div className="scaleui scale-tailwind scale-theme w-full flex justify-center" ref={myRef}>
      <Head title={`${substageDefinition.name} Substage on ${substageDefinition.project.name}`} />
      <div className="flex flex-col gap-4 max-w-full">
        <Typography.H1>Substage Definition</Typography.H1>
        <div>
          <span className="font-medium text-lg">{substageDefinition.name} </span>
          <WithCopy className="inline-block" copyText={substageDefinition._id}>
            {substageDefinition._id}
          </WithCopy>
        </div>
        {width && width >= DOUBLE_COLUMN_MIN_WIDTH ? (
          <div className="flex gap-4 min-w-0">
            <div className="flex flex-col gap-4 min-w-0 w-1/3 overflow-wrap-anywhere">
              {leftColumnElements}
            </div>
            <div className="flex flex-col gap-4 min-w-0 w-2/3 overflow-wrap-anywhere">
              {rightColumnElements}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4 min-w-0 overflow-wrap-anywhere">
            {leftColumnElements}
            {rightColumnElements}
          </div>
        )}
        <style jsx>{`
          .overflow-wrap-anywhere {
            overflow-wrap: anywhere;
          }
        `}</style>
      </div>
    </div>
  );
};

export default SubstageDefinitionLookupView;
